import { useState, useEffect } from "react";
import api from "../../utils/api";

const WASTE_BUCKETS = [
  { key: "T", name: "Transport", desc: "Unnecessary movement of information, documents, or work items", color: "bg-blue-50 border-blue-200 text-blue-700", icon: "T" },
  { key: "I", name: "Inventory", desc: "Excess WIP, backlogs, or cases waiting to be processed", color: "bg-orange-50 border-orange-200 text-orange-700", icon: "I" },
  { key: "M", name: "Motion", desc: "Unnecessary system navigation, screen-switching, manual lookups", color: "bg-purple-50 border-purple-200 text-purple-700", icon: "M" },
  { key: "W", name: "Waiting", desc: "Time lost awaiting approvals, information, or decisions", color: "bg-red-50 border-red-200 text-red-700", icon: "W" },
  { key: "O1", name: "Overprocessing", desc: "Doing more than required — excess checks, duplicate steps", color: "bg-yellow-50 border-yellow-200 text-yellow-700", icon: "O" },
  { key: "O2", name: "Overproduction", desc: "Producing outputs before they are needed", color: "bg-amber-50 border-amber-200 text-amber-700", icon: "O" },
  { key: "D", name: "Defects", desc: "Errors, rework, corrections, and quality failures", color: "bg-red-50 border-red-200 text-red-700", icon: "D" },
  { key: "S", name: "Skills (Under-utilisation)", desc: "Failing to use full people capability — misaligned roles, under-delegation", color: "bg-sage-50 border-sage-200 text-sage-700", icon: "S" },
];

const PROMPT_GUIDE = [
  { bucket: "Transport", questions: ["Where are work items physically or digitally handed off from one team to another?", "Which approvals require a physical signature or manual email chain?", "How many times does information change format between systems?", "Are there steps where the same document is handled by more than two people?"] },
  { bucket: "Inventory", questions: ["Where do cases or requests queue before being picked up?", "What is the current WIP volume and average waiting time at each stage?", "Are there batch processing points that could be made continuous flow?", "How many cases are 'on hold' awaiting information, and for how long?"] },
  { bucket: "Motion", questions: ["How many screens/systems does an agent navigate for a single transaction?", "Which data is keyed in more than once across different platforms?", "What manual lookups are performed that could be automated?", "How much time is spent searching for information vs. processing it?"] },
  { bucket: "Waiting", questions: ["What are the top 5 reasons a case is put on hold or escalated?", "Which external parties cause the most processing delays?", "How often are cases delayed waiting for manager approval?", "What percentage of cases breach SLA due to a waiting step?"] },
  { bucket: "Overprocessing", questions: ["Which process steps are applied uniformly regardless of case complexity?", "Are there checks that add no value for certain case types?", "Which approval steps exist that could be delegated or automated?", "What regulatory requirements are being exceeded beyond what is needed?"] },
  { bucket: "Overproduction", questions: ["What reports are generated on a schedule rather than on demand?", "Which outputs are prepared before downstream capacity is available?", "Are there cases pre-processed that frequently don't proceed?", "What analysis is routinely produced that no one acts upon?"] },
  { bucket: "Defects", questions: ["What are the top 5 error categories causing rework?", "At which stage do most quality failures occur?", "What percentage of rework is caused by upstream errors?", "Are there automated validations in place at key error-prone steps?"] },
  { bucket: "Skills (Under-utilisation)", questions: ["Are experienced staff spending time on tasks a junior could handle?", "Which team members have capabilities not currently being used?", "Are role definitions aligned to actual work content and complexity?", "What decisions require escalation that could be delegated?"] },
];

export default function TimwoodTab({ project }) {
  const [domainData, setDomainData] = useState(null);
  const [activeTab, setActiveTab] = useState("waste");
  const domain = project?.setup?.org_profile?.domain || "Other / Generic";

  useEffect(() => {
    api.get("/domains").then((res) => {
      setDomainData(res.data.timwood || {});
    }).catch(() => {});
  }, []);

  const timwood = domainData?.[domain] || {};

  return (
    <div className="animate-fade-in">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Step 7</p>
        <h2 className="font-heading text-2xl font-semibold text-sage-900">TIMWOOD Waste Analysis</h2>
        <p className="text-sage-500 text-sm mt-1">
          Domain-specific lean waste framework for <span className="font-semibold text-sage-700">{domain}</span>. Use these insights to guide waste walk facilitation and Kaizen events.
        </p>
      </div>

      <div className="flex gap-2 mb-4">
        {[{ key: "waste", label: "Waste Examples" }, { key: "guide", label: "Facilitation Prompt Guide" }].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            data-testid={`timwood-tab-${key}`}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${activeTab === key ? "bg-sage-600 text-white" : "border border-sage-200 text-sage-600 hover:bg-sage-50"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === "waste" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {WASTE_BUCKETS.map((bucket) => {
            const examples = timwood[bucket.key] || [];
            return (
              <div key={bucket.key} className={`rounded-md p-4 border ${bucket.color}`} data-testid={`timwood-bucket-${bucket.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-md flex items-center justify-center font-heading text-base font-bold bg-white/60">{bucket.icon}</span>
                  <div>
                    <h4 className="font-heading text-sm font-semibold">{bucket.name}</h4>
                    <p className="text-xs opacity-75 leading-snug">{bucket.desc}</p>
                  </div>
                </div>
                {examples.length > 0 ? (
                  <ul className="space-y-1 mt-2">
                    {examples.map((ex, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs opacity-90">
                        <span className="mt-1 w-1 h-1 rounded-full bg-current flex-shrink-0" />
                        {ex}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs opacity-60 italic mt-1">Loading domain-specific examples...</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "guide" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PROMPT_GUIDE.map((guide) => (
            <div key={guide.bucket} className="bg-white border border-[#E2E8E3] rounded-md p-4">
              <h4 className="font-heading text-sm font-semibold text-sage-800 mb-3 pb-2 border-b border-sage-50">{guide.bucket}</h4>
              <ul className="space-y-2">
                {guide.questions.map((q, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-sage-600">
                    <span className="text-sage-400 font-bold mt-0.5">Q{i + 1}.</span>
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
